/**
 * PunchRoomGuidanceEditor — room/area-type punch guidance checklists (Construction Gates Phase D
 * trimmings). A tab inside TemplatesWorkspace.jsx (consistent with the checklist-templates
 * module): a reference checklist per room/area type (Kitchen, Primary Bath, Exterior/Envelope, …)
 * for luxury-residential inspection consistency — a Supabase table, editable here, no deploy
 * required. AI-assisted suggestions are a deliberately deferred v2.
 *
 * Self-contained list (mirrors BimTemplatesList's own grouped-list-with-inline-add pattern) —
 * no separate drill-down editor screen; each type expands in place to show/edit its items.
 */
import { useState, useEffect } from 'react'
import { S } from './TemplatesWorkspace.jsx'
import { confirmDialog } from './Dialog.jsx'
import {
  getPunchRoomGuidanceTypes, createPunchGuidanceType, renamePunchGuidanceType,
  deletePunchGuidanceType, movePunchGuidanceType, createPunchGuidanceItem,
  updatePunchGuidanceItem, deletePunchGuidanceItem, movePunchGuidanceItem,
} from '../services/punchRoomGuidance.js'

function GuidanceItemRow({ item, onSave, onDelete, onMove, isFirst, isLast }) {
  const [text, setText] = useState(item.text)
  const dirty = text.trim() !== item.text && text.trim().length > 0

  async function commit() {
    const t = text.trim()
    if (!t || t === item.text) { setText(item.text); return }
    await onSave(t)
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '5px 4px', borderBottom: '0.5px solid var(--aj-border)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 3 }}>
        <button onClick={() => onMove('up')} disabled={isFirst} title="Move up"
          style={{ background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 10, padding: 0, lineHeight: 1 }}>▲</button>
        <button onClick={() => onMove('down')} disabled={isLast} title="Move down"
          style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', color: isLast ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 10, padding: 0, lineHeight: 1 }}>▼</button>
      </div>
      <textarea
        value={text}
        rows={Math.max(1, Math.ceil(text.length / 90))}
        onChange={e => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.target.blur() } }}
        style={{
          flex: 1, border: 'none', outline: 'none', resize: 'none', background: 'transparent',
          fontSize: 12.5, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)', lineHeight: 1.45,
          borderBottom: dirty ? '1px dashed var(--aj-border-mid)' : '1px solid transparent',
        }}
      />
      <button onClick={onDelete} title="Delete item" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 13, padding: '2px 4px' }}>×</button>
    </div>
  )
}

function GuidanceTypeRow({ type, expanded, onToggle, onRename, onDelete, onMove, onItemsChange, isFirst, isLast, addToast }) {
  const [name, setName] = useState(type.name)
  const [newText, setNewText] = useState('')
  const [adding, setAdding] = useState(false)

  useEffect(() => { setName(type.name) }, [type.name])

  async function commitName() {
    const n = name.trim()
    if (!n || n === type.name) { setName(type.name); return }
    try { await onRename(n) } catch (e) { addToast('Rename failed: ' + (e.message || 'unknown error')); setName(type.name) }
  }

  async function addItem() {
    const t = newText.trim()
    if (!t || adding) return
    setAdding(true)
    try {
      const item = await createPunchGuidanceItem(type.id, t)
      onItemsChange([...type.items, item])
      setNewText('')
    } catch (e) { addToast('Add failed: ' + (e.message || 'unknown error')) } finally { setAdding(false) }
  }

  async function saveItem(item, text) {
    try {
      await updatePunchGuidanceItem(item.id, text)
      onItemsChange(type.items.map(i => i.id === item.id ? { ...i, text } : i))
    } catch (e) { addToast('Save failed: ' + (e.message || 'unknown error')) }
  }

  async function deleteItem(item) {
    if (!(await confirmDialog({ title: 'Delete item?', message: 'Delete "' + item.text + '"?', confirmLabel: 'Delete', danger: true }))) return
    try {
      await deletePunchGuidanceItem(item.id)
      onItemsChange(type.items.filter(i => i.id !== item.id))
    } catch (e) { addToast('Delete failed: ' + (e.message || 'unknown error')) }
  }

  async function moveItem(item, direction) {
    const idx = type.items.findIndex(i => i.id === item.id)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= type.items.length) return
    const next = [...type.items]
    ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
    onItemsChange(next)
    try { await movePunchGuidanceItem(item.id, direction) } catch (e) { addToast('Reorder failed') }
  }

  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 10px 8px', borderBottom: '0.5px solid var(--aj-border-mid)' }}>
        <button onClick={onToggle} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 11, padding: 0, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</button>
        <input value={name} onChange={e => setName(e.target.value)} onBlur={commitName}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
          style={{ ...S.input, width: 220, fontWeight: 500, fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'Montserrat, sans-serif', border: 'none', background: 'transparent', padding: '2px 0' }} />
        <span style={{ fontSize: 10.5, fontFamily: 'DM Mono, monospace', color: 'var(--aj-text-muted)' }}>{type.items.length} item{type.items.length !== 1 ? 's' : ''}</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => onMove('up')} disabled={isFirst} title="Move type up"
          style={{ background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 12, padding: '0 3px' }}>▲</button>
        <button onClick={() => onMove('down')} disabled={isLast} title="Move type down"
          style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', color: isLast ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 12, padding: '0 3px' }}>▼</button>
        <button onClick={onDelete} title="Delete type" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 14, padding: '0 4px' }}>×</button>
      </div>
      {expanded && (
        <div style={{ padding: '4px 10px 10px 30px' }}>
          {type.items.map((item, i) => (
            <GuidanceItemRow key={item.id} item={item}
              isFirst={i === 0} isLast={i === type.items.length - 1}
              onSave={text => saveItem(item, text)}
              onDelete={() => deleteItem(item)}
              onMove={dir => moveItem(item, dir)} />
          ))}
          {type.items.length === 0 && (
            <div style={{ padding: '8px 0', fontSize: 12, fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No guidance items yet.</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-start' }}>
            <textarea value={newText} rows={1} placeholder="Add a guidance item…"
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addItem() } }}
              style={{ ...S.input, flex: 1, resize: 'vertical' }} />
            <button onClick={addItem} disabled={adding || !newText.trim()} style={S.btnSecondary}>{adding ? 'Adding…' : '+ Add'}</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function PunchRoomGuidanceList({ addToast }) {
  const [types, setTypes] = useState(null)   // null = loading
  const [expanded, setExpanded] = useState({})
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => { getPunchRoomGuidanceTypes().then(setTypes).catch(() => setTypes([])) }, [])

  async function handleCreate() {
    const name = newName.trim()
    if (!name || creating) return
    setCreating(true)
    try {
      const t = await createPunchGuidanceType(name)
      setTypes(prev => [...(prev || []), t])
      setExpanded(e => ({ ...e, [t.id]: true }))
      setNewName('')
      addToast('Guidance type created')
    } catch (e) { addToast('Create failed: ' + (e.message || 'unknown error')) } finally { setCreating(false) }
  }

  function patchType(id, patch) {
    setTypes(prev => (prev || []).map(t => t.id === id ? { ...t, ...patch } : t))
  }

  async function handleDelete(t) {
    if (!(await confirmDialog({
      title: 'Delete guidance type?',
      message: `Delete "${t.name}" and its ${t.items.length} checklist item(s)? This cannot be undone.`,
      confirmLabel: 'Delete', danger: true,
    }))) return
    try {
      await deletePunchGuidanceType(t.id)
      setTypes(prev => (prev || []).filter(x => x.id !== t.id))
      addToast('Guidance type deleted')
    } catch (e) { addToast('Delete failed: ' + (e.message || 'unknown error')) }
  }

  async function handleMoveType(t, direction) {
    const list = types || []
    const idx = list.findIndex(x => x.id === t.id)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= list.length) return
    const next = [...list]
    ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
    setTypes(next)
    try { await movePunchGuidanceType(t.id, direction) } catch (e) { addToast('Reorder failed') }
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '0 24px 12px', flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          style={{ ...S.input, width: 260, display: 'inline-block' }}
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
          placeholder="New room/area type name…"
        />
        <button onClick={handleCreate} disabled={creating || !newName.trim()} style={S.btnPrimary}>{creating ? 'Creating…' : '+ New Type'}</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
        {types === null && (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>Loading guidance types…</div>
        )}
        {Array.isArray(types) && types.length === 0 && (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
            No room/area guidance types yet — add one above (e.g. Kitchen, Primary Bath, Exterior/Envelope).
          </div>
        )}
        {(types || []).map((t, i) => (
          <GuidanceTypeRow
            key={t.id}
            type={t}
            expanded={!!expanded[t.id]}
            onToggle={() => setExpanded(e => ({ ...e, [t.id]: !e[t.id] }))}
            onRename={name => renamePunchGuidanceType(t.id, name).then(() => patchType(t.id, { name }))}
            onDelete={() => handleDelete(t)}
            onMove={dir => handleMoveType(t, dir)}
            onItemsChange={items => patchType(t.id, { items })}
            isFirst={i === 0}
            isLast={i === (types || []).length - 1}
            addToast={addToast}
          />
        ))}
      </div>
    </div>
  )
}
